import React from 'react';

import { Button, Tooltip } from '@bringg/react-components';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { PrivilegeTypes } from '@bringg/types';
import { useMerchantCurrency } from '@bringg-frontend/hooks';

import { PLANNED_DELIVERY_WINDOWS_MAX_NAMES_BEFORE_COUNT } from '../../../constants';
import { PlannedDeliveryWindowsEventType } from '../../planned-delivery-windows-calendar';
import { useStores } from '../../../../../recipes';
import { Cutoff } from '../../../cutoff/cutoff';
import { useHasAccess } from 'bringg-web';

import styles from './planned-delivery-windows-popover-content.module.scss';

interface Props {
	title: string;
	event: PlannedDeliveryWindowsEventType;
	setPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
	openModalWithPlannedDeliveryWindowSlot: (plannedDeliveryWindowId: number) => void;
	openRecurrenceTypeModalWithDeleteMode: (id: number) => void;
	fillRatio: string;
	timezone: string;
}

const PlannedDeliveryWindowsPopoverContent = ({
	title,
	event,
	openModalWithPlannedDeliveryWindowSlot,
	openRecurrenceTypeModalWithDeleteMode,
	fillRatio,
	timezone
}: Props) => {
	const { servicePlansStore, serviceArea, merchantConfigurationsStore } = useStores();
	const hasAccessToEdit = useHasAccess(PrivilegeTypes.EDIT_PLANNED_DELIVERY_WINDOWS);
	const { t } = useTranslation();
	const { symbol: currencySymbol } = useMerchantCurrency();
	const manageFeeEnabled = merchantConfigurationsStore.configuration?.manage_delivery_windows_fee;

	const getRelationLine = (
		ids: number[],
		labelTranslationKey: string,
		countTranslationKey: string,
		getName: (id: number) => string
	) => {
		if (!ids?.length) {
			return null;
		}

		const count = ids.length;

		if (count > PLANNED_DELIVERY_WINDOWS_MAX_NAMES_BEFORE_COUNT) {
			return <span>{t(countTranslationKey, { count })}</span>;
		}

		const names = ids.map(getName).filter(Boolean);
		if (!names.length) {
			return null;
		}

		return <span>{t(labelTranslationKey, { count: names.length, names: names.join(', ') })}</span>;
	};

	return (
		<div className={styles.popoverContent} onClick={e => e.stopPropagation()}>
			<div className={styles.header}>
				<div className={styles.title}>
					<Tooltip title={title}>{title}</Tooltip>
				</div>
				{hasAccessToEdit ? (
					<div className={styles.actions}>
						<Tooltip title={t('GLOBAL.EDIT')}>
							<Button
								icon={<BringgIcon iconName={BringgFontIcons.ShowMore} />}
								className={styles.gray600}
								shape="circle"
								type="text"
								onClick={() => openModalWithPlannedDeliveryWindowSlot(event.id)}
								data-test-id="planned-delivery-window-popover-content-edit"
							/>
						</Tooltip>
						<Tooltip placement="top" title={t('GLOBAL.DELETE')}>
							<Button
								icon={<BringgIcon iconName={BringgFontIcons.Trash} />}
								className={styles.gray600}
								shape="circle"
								type="text"
								onClick={() => openRecurrenceTypeModalWithDeleteMode(event.id)}
								data-test-id="planned-delivery-window-popover-content-delete"
							/>
						</Tooltip>
					</div>
				) : null}
			</div>
			<div className={styles.popoverContentBody}>
				{event.cutoff !== 0 && (
					<Cutoff startWindowTime={event.start} cutoff={event.cutoff} timezone={timezone} />
				)}
				{`${t('PLANNED_DELIVERY_WINDOWS_POPOVER_CONTENT.FILL_RATIO')}: ${fillRatio}`}
				<div className={styles.services}>
					{getRelationLine(
						event.service_plan_ids,
						'PLANNED_DELIVERY_WINDOWS_SLOT_CARD.SERVICE_PLAN_LABEL',
						'PLANNED_DELIVERY_WINDOWS_SLOT_CARD.SERVICE_PLANS_COUNT',
						(id: number) => servicePlansStore.servicePlans.get(id)?.name
					)}
					{getRelationLine(
						event.service_area_ids,
						'PLANNED_DELIVERY_WINDOWS_SLOT_CARD.SERVICE_AREA_LABEL',
						'PLANNED_DELIVERY_WINDOWS_SLOT_CARD.SERVICE_AREAS_COUNT',
						(id: number) => serviceArea.get(id)?.name
					)}
					{manageFeeEnabled && event.fee ? (
						<span data-test-id="planned-delivery-window-popover-content-fee">
							{`${t('PLANNED_DELIVERY_WINDOWS.POPOVER.FEE')}: ${currencySymbol}${event.fee.toFixed(2)}`}
						</span>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default observer(PlannedDeliveryWindowsPopoverContent);
